import type { QueryClient } from '@tanstack/react-query';

import type { NodeDeletionResult } from 'api/data/client-dataapi';
import { findNodeDetailVariants, syncNodeDetailVariant } from 'api/data/events/nodeDetailCache';
import { selectNodeEventProperties } from 'api/data/events/nodeEventProperties';
import type { DataNodeUpdatedEvent } from 'api/data/events/signalREvents';
import { nodeDetailQueryKeys } from 'api/data/hooks/nodeDetailQueryKeys';
import type { DefaultCasedOutputOperationResult } from 'api/outputOperationResult';
import type { DataNodeDeletedEvent, EventCompletion } from 'state/signalr/signalrState';
import { normalizeId } from 'utils/ids';

/**
 * Live-sync the node details panel from a `DataNodeUpdated` event.
 *
 * The panel can hold several cached copies of one node — the live view plus one per open proposal —
 * while the event carries a single payload: the live property bag plus the overrides each proposal
 * puts on top of it. So each copy is updated with the values that copy is meant to show.
 */
export function syncNodeDetailFromEvent(event: DataNodeUpdatedEvent, queryClient: QueryClient): void {
  const { Id, Properties, Proposals } = event.Payload;

  if (!Properties) return;
  const variants = findNodeDetailVariants(queryClient, event.ProjectId, Id);
  if (variants.length === 0) return;

  for (const variant of variants) {
    const properties = selectNodeEventProperties(Properties, Proposals, variant.proposalId);

    syncNodeDetailVariant(queryClient, variant, properties);
  }
}

/**
 * Drop the cached details of every node a `DataNodeDeleted` event removed. Resetting (not just invalidating)
 * clears the data, so an open panel's refetch fails with nothing loaded and its error handling closes it;
 * a closed one stops serving a node that's gone.
 */
export function invalidateDeletedNodeDetails(
  event: EventCompletion<DataNodeDeletedEvent, DefaultCasedOutputOperationResult<NodeDeletionResult>>,
  queryClient: QueryClient
) {
  const { Success, Result } = event.Result;
  // A failed or not-yet-confirmed (cascading) delete removed nothing.
  if (!Success || Result.NeedsConfirmation) return;

  const matchers = Object.entries(Result.IsNodeMap)
    .filter(([, isNode]) => isNode)
    .map(([id]) => nodeDetailQueryKeys.nodeMatcher(event.DataEvent.ProjectId, id));
  // A delete inside a proposal leaves the node in live and other proposals; a live delete removes it everywhere.
  const { ProposalId } = event.DataEvent;
  const isDeletedVariant = (queryKey: readonly unknown[]) =>
    !ProposalId || normalizeId(nodeDetailQueryKeys.proposalIdOf(queryKey) ?? '') === normalizeId(ProposalId);

  queryClient.resetQueries({
    predicate: (query) => isDeletedVariant(query.queryKey) && matchers.some((matches) => matches(query.queryKey))
  });
}
